import _ from 'lodash';
import moment from 'moment';
import {
  KEY_CANCEL,
  LIST_EVENT_SKIP_ON_RUN_EVENT,
  LIST_RUN_EVENT,
  RHYTHM_EVENT_LIST_CAN_EDIT,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import type { IEvent } from '@/shared/model';
import fetchHolterEvents from '@/entities/event/api/services/fetchHolterEvents';
import { checkAndProcessBeatData } from '@/entities/beat/@x/event';
import { EVENT_OTHER_TYPE } from '../model/eventMessages';
import formatEventStrip from './formatEventStrip';

/**
 * Detects whether a technician edit altered an ECG event's reviewed flag, span, type, comments or captured strips. Compares the two events field by field through optional chaining, with a deep equality check on stripImgs, and reports a difference as soon as one is found.
 *
 * @param originalEvent - The event as it was before editing.
 * @param changedEvent - The event carrying the technician's edits.
 * @returns True when any tracked field differs between the two events.
 */
export const checkEventDataChange = (originalEvent: any, changedEvent: any) => {
  if (
    originalEvent?.isReviewed !== changedEvent?.isReviewed ||
    originalEvent?.start !== changedEvent?.start ||
    originalEvent?.stop !== changedEvent?.stop ||
    originalEvent?.type !== changedEvent?.type ||
    originalEvent?.technicianComments !== changedEvent?.technicianComments ||
    !_.isEqual(originalEvent?.stripImgs, changedEvent?.stripImgs)
  ) {
    return true;
  }
  return false;
};

/**
 * Counts the beats falling inside an added or updated event's span and the epoch range they cover. Awaits the decoded holter beat samples for the window, removes samples whose local change marks them deleted, unions the remaining local beat changes with them, then derives the earliest and latest beat epoch and the seconds between them.
 *
 * @param config - The event span, the local beat changes, the sampling frequency and the ECG data map to decode from.
 * @returns A promise resolving to the merged beat count, the span in seconds, and the first and last beat epochs.
 */
export const getBeatCountAddUpdateEvents = async (config: {
  start: string | number | Date;
  stop: string | number | Date;
  localBeatChanges?: any[];
  samplingFrequency: number;
  ecgDataMap: any;
}) => {
  const { start, stop, localBeatChanges = [], samplingFrequency, ecgDataMap } = config;
  const newEventStart = new Date(start).getTime();
  const newEventStop = new Date(stop).getTime();
  // `checkAndProcessBeatData` can resolve `null`; preserved as-is (would throw here on null, same as before typing).
  const { beats: holterBeatsNewEvent, beatEpochs } = (await checkAndProcessBeatData({
    ecgDataMap,
    momentObject: {
      startMoment: moment(start).toISOString(),
      stopMoment: moment(stop).toISOString(),
    },
  }))!;

  const localBeatChangeInRangeNewEventWithoutDelete: number[] = [];
  const localBeatChangeInRangeNewEvent = localBeatChanges.filter(
    (beat) => beat.id >= newEventStart && beat.id <= newEventStop,
  );
  const beatChangeEpoch: number[] = [];
  localBeatChangeInRangeNewEvent.forEach((beat) => {
    const beatSample = ((beat.id - newEventStart) / 1000) * samplingFrequency;
    if (beat.type === TYPE_BEAT_ENUM.DELETED) {
      const index = _.findIndex(holterBeatsNewEvent, (sample) => sample === beatSample);
      if (index !== -1) {
        holterBeatsNewEvent.splice(index, 1);
        beatEpochs.splice(index, 1);
      }
    } else {
      localBeatChangeInRangeNewEventWithoutDelete.push(beatSample);
      beatChangeEpoch.push(beat.id);
    }
  });
  const newMergeBeats = _.union(localBeatChangeInRangeNewEventWithoutDelete, holterBeatsNewEvent);
  const sortBeatChangeEpochs = _.sortBy(beatChangeEpoch);
  const startBeat =
    sortBeatChangeEpochs[0] < beatEpochs[0] ? sortBeatChangeEpochs[0] : beatEpochs[0];
  const stopBeat =
    sortBeatChangeEpochs[sortBeatChangeEpochs.length - 1] > beatEpochs[beatEpochs.length - 1]
      ? sortBeatChangeEpochs[sortBeatChangeEpochs.length - 1]
      : beatEpochs[beatEpochs.length - 1];
  const diffEpoch = stopBeat - startBeat;
  const diffBeatBySecond = diffEpoch / 1000;
  return {
    mergeBeatCount: newMergeBeats.length,
    diffBeatBySecond,
    startBeat,
    stopBeat,
  };
};

/**
 * Loads the holter events touching both edges of a time window so added or updated events overlapping its boundaries are known. Skips the fetch entirely in the 100 percent AFIB mode, issues a single query for windows under an hour, and otherwise fetches two five-second windows at the start and stop in parallel, dedupes by id and normalises the payload for strip display.
 *
 * @param info - The study and profile ids, the search window, the requested types and the AFIB mode flag.
 * @returns A promise resolving to the formatted edge events, or an empty array in AFIB mode.
 */
export const getEventBothSideAddUpdateEvents = async (info: {
  studyId?: string;
  profileId?: string;
  startSearchDate?: string;
  stopSearchDate?: string;
  isUse100Afib?: boolean;
  types?: any[];
}) => {
  const { studyId, profileId, startSearchDate, stopSearchDate, isUse100Afib, types } = info || {};
  if (isUse100Afib) return [];
  const duration = moment(stopSearchDate).diff(moment(startSearchDate), 'seconds');
  if (duration < 3600) {
    // 1 hour
    const filter = {
      studyId,
      profileId,
      startSearchDate,
      stopSearchDate,
      types,
    };
    const { events } = await fetchHolterEvents(filter, 0, true, KEY_CANCEL.API_MODAL, true);
    return formatEventStrip(events);
  }
  const filterStart = {
    studyId,
    profileId,
    startSearchDate,
    stopSearchDate: moment(startSearchDate).add(5, 'seconds').toISOString(),
    types,
  };
  const filterEnd = {
    studyId,
    profileId,
    startSearchDate: stopSearchDate
      ? moment(stopSearchDate).subtract(5, 'seconds').toISOString()
      : undefined,
    stopSearchDate,
    types,
  };
  const promises = [
    fetchHolterEvents(filterStart, 0, true, KEY_CANCEL.API_MODAL, true),
    fetchHolterEvents(filterEnd, 0, true, KEY_CANCEL.API_MODAL, true),
  ];
  const [eventsStart, eventsEnd] = await Promise.all(promises);
  const events = _.uniqBy([...(eventsStart?.events || []), ...(eventsEnd?.events || [])], 'id');
  const dataFormat = formatEventStrip(events);
  return dataFormat;
};

const canKeepEventsWithAfibType = [TYPE_RHYTHM_EVENT_ENUM.VT, TYPE_RHYTHM_EVENT_ENUM.PAUSE];

const RHYTHM_BEATS_EVENT_LIST_CAN_EDIT = [
  ...RHYTHM_EVENT_LIST_CAN_EDIT,
  TYPE_BEAT_EVENT_ENUM.SINGLE_SVE,
  TYPE_BEAT_EVENT_ENUM.SVE_RUN,
  TYPE_BEAT_EVENT_ENUM.SVE_COUPLET,
  TYPE_BEAT_EVENT_ENUM.SVE_BIGEMINY,
  TYPE_BEAT_EVENT_ENUM.SVE_TRIGEMINAL,
  TYPE_BEAT_EVENT_ENUM.SVE_QUADRIGEMINY,
];

/**
 * Decides whether an existing ECG event is superseded by an overlapping newly added or duration-edited event. Returns any recorded change outright when one exists or the type is not editable, otherwise scans the new events then the duration-changed ones for overlap, skipping VT and SVT run-event pairings and AFIB combinations that may coexist, and clones the event as DELETED with the linking event on a run-event match, same-type match or full containment.
 *
 * @param event - The event being checked for supersession, whose oldType drives the pairing rules.
 * @param eventNews - The locally added events not yet saved.
 * @param eventChanges - The locally edited events not yet saved, defaulting to none.
 * @returns An object holding the superseded event clone plus linkEventNew and linkEventChange, or an empty object when the event has no idEvent.
 */
export const getEventEffectdByUpdatedEvent = (
  event: IEvent,
  eventNews: IEvent[],
  eventChanges: IEvent[] = [],
) => {
  if (!event.idEvent) return {};
  const findEventChange = _.find(eventChanges, (x) => x.idEvent === event.idEvent);
  if (!_.includes(RHYTHM_BEATS_EVENT_LIST_CAN_EDIT, event.type)) return { event: findEventChange };
  if (findEventChange) return { event: findEventChange };
  let isChange = false;
  let linkEventNew: IEvent | {} = {};
  let linkEventChange: IEvent | {} = {};
  const cloneEvent = _.cloneDeep(event);
  const eventStart = moment(event.start).valueOf();
  const eventStop = moment(event.stop).valueOf();
  _.forEach(eventNews, (eventNew) => {
    const { start, stop } = eventNew;
    const startEpoch = moment(start).valueOf();
    const stopEpoch = moment(stop).valueOf();
    if (
      (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(eventNew.type as any) &&
        LIST_RUN_EVENT.includes(event.oldType as any)) ||
      (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(event.oldType as any) &&
        LIST_RUN_EVENT.includes(eventNew.type as any))
    ) {
      //* VT/SVT không cần check overlap cho 1 số event type khác
      return;
    }
    if (
      eventStart <= stopEpoch &&
      eventStop >= startEpoch &&
      _.includes(RHYTHM_BEATS_EVENT_LIST_CAN_EDIT, eventNew.type)
    ) {
      if (
        (event.oldType === TYPE_BEAT_EVENT_ENUM.VE_RUN &&
          eventNew.type === TYPE_RHYTHM_EVENT_ENUM.VT) ||
        (event.oldType === TYPE_BEAT_EVENT_ENUM.SVE_RUN &&
          eventNew.oldType === TYPE_RHYTHM_EVENT_ENUM.SVT)
      ) {
        _.assign(cloneEvent, { type: TYPE_BEAT_ENUM.DELETED });
        isChange = true;
        linkEventNew = eventNew;
        return false;
      }
      if (event.oldType === eventNew.type) {
        // cùng loại chỉ giữ mới xoá cũ
        _.assign(cloneEvent, { type: TYPE_BEAT_ENUM.DELETED });
        isChange = true;
        linkEventNew = eventNew;
        return false;
      }
      if (
        !(
          (eventNew.type === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
            canKeepEventsWithAfibType.includes(event.oldType as any)) ||
          (event.oldType === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
            canKeepEventsWithAfibType.includes(eventNew.type as any)) ||
          (eventNew.type === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
            event.oldType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT)
        ) &&
        eventStart >= startEpoch &&
        eventStop <= stopEpoch &&
        _.includes(RHYTHM_BEATS_EVENT_LIST_CAN_EDIT, event.type)
      ) {
        // khác loại chỉ xoá khi mới chứa cũ, mới cắt cũ thì giữ, AI tự cắt
        _.assign(cloneEvent, { type: TYPE_BEAT_ENUM.DELETED });
        isChange = true;
        linkEventNew = eventNew;
        return false;
      }
    }
  });
  if (!isChange) {
    // change duration
    _.forEach(eventChanges, (eventChange) => {
      const { start, stop } = eventChange || {};
      const startEpoch = moment(start).valueOf();
      const stopEpoch = moment(stop).valueOf();
      const { originalData } = eventChange || {};
      const isChangeDuration =
        originalData?.start !== event.start || originalData?.stop !== event.stop;
      if (
        (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(eventChange.type as any) &&
          LIST_RUN_EVENT.includes(event.oldType as any)) ||
        (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(event.oldType as any) &&
          LIST_RUN_EVENT.includes(eventChange.type as any))
      ) {
        //* VT/SVT không cần check overlap cho 1 số event type khác
        return;
      }
      if (
        eventStart < stopEpoch &&
        eventStop > startEpoch &&
        // `eventChange.type` is TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM, compared here against the
        // unrelated TYPE_BEAT_ENUM enum — pre-existing runtime string comparison, kept as-is.
        (eventChange.type as any) !== TYPE_BEAT_ENUM.DELETED &&
        eventChange.idEvent !== event.idEvent &&
        _.includes(RHYTHM_BEATS_EVENT_LIST_CAN_EDIT, eventChange.oldType) &&
        isChangeDuration
      ) {
        if (
          (event.oldType === TYPE_BEAT_EVENT_ENUM.VE_RUN &&
            eventChange.type === TYPE_RHYTHM_EVENT_ENUM.VT) ||
          (event.oldType === TYPE_BEAT_EVENT_ENUM.SVE_RUN &&
            eventChange.oldType === TYPE_RHYTHM_EVENT_ENUM.SVT)
        ) {
          _.assign(cloneEvent, { type: TYPE_BEAT_ENUM.DELETED });
          isChange = true;
          linkEventChange = eventChange;
          return false;
        }
        if (event.oldType === eventChange.type) {
          _.assign(cloneEvent, { type: TYPE_BEAT_ENUM.DELETED });
          isChange = true;
          linkEventChange = eventChange;
          return false;
        }
        if (
          !(
            (eventChange.type === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
              canKeepEventsWithAfibType.includes(event.oldType as any)) ||
            (event.oldType === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
              canKeepEventsWithAfibType.includes(eventChange.type as any)) ||
            (eventChange.type === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
              event.oldType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT)
          ) &&
          eventStart > startEpoch &&
          eventStop < stopEpoch &&
          isChangeDuration &&
          _.includes(RHYTHM_BEATS_EVENT_LIST_CAN_EDIT, event.type)
        ) {
          _.assign(cloneEvent, { type: TYPE_BEAT_ENUM.DELETED });
          isChange = true;
          linkEventChange = eventChange;
          return false;
        }
      }
    });
  }
  return {
    event: isChange ? cloneEvent : undefined,
    linkEventNew,
    linkEventChange,
  };
};

/**
 * Resolves supersession for a whole list of ECG events in one pass. Walks the events in order, checking each one against the same pending added and duration-changed events to see whether an overlap marks it DELETED, and collects every result.
 *
 * @param events - The events to check for supersession.
 * @param eventNews - The locally added events not yet saved.
 * @param eventChanges - The locally edited events not yet saved, defaulting to none.
 * @returns An array of per-event results, each carrying the superseded clone and its linking events.
 */
export const getMultipleEventEffectdByUpdatedEvent = (
  events: IEvent[],
  eventNews: IEvent[],
  eventChanges: IEvent[] = [],
) => {
  const eventsHasDeleted: ReturnType<typeof getEventEffectdByUpdatedEvent>[] = [];
  _.forEach(events, (event) => {
    const deletedEvent = getEventEffectdByUpdatedEvent(event, eventNews, eventChanges);
    eventsHasDeleted.push(deletedEvent);
  });
  return eventsHasDeleted;
};

/**
 * Resolves the event type that drives type selection in the event UI. Falls back to the shared other-event constant when the event is missing or carries no type.
 *
 * @param event - The event whose type is being selected, optional.
 * @returns The event's type, or the other-event constant.
 */
export const getSelectedEventType = (event?: IEvent) => event?.type || EVENT_OTHER_TYPE;
